import type { JSONContent } from '@tiptap/core'
// eslint-disable-next-line tp/forbid-import-composable-to-service,tp/using-vue-in-services-restriction
import type { Ref } from '@/composition/vue/compositionApi'
import type { TmWrappers } from '@/wrappers'
import type MessagesAttachmentFile from '@/data/models/domain/MessagesAttachmentFile'
import type {
  Months,
  WeekDays,
  WeekDaysFull,
  ServicePhoneType,
  TmCountryCode,
  MonthlyRepeat,
  YearlyType,
  HoursIntervalType,
} from '@/services/types'
import type { SmsInfo } from '@/utils/smsInfo'
import type { InteractiveOption } from '@/components/shared/types'
import type { Nullable } from '@/types'
import type MessagesPrice from '@/data/models/domain/MessagesPrice'
import type { SingleModelResolverParams } from '@/services/resolvers/types'
import type { ChatDirection, ChatType } from '@/data/models/domain/chats/types'
import type BaseModel from '@/data/models/BaseModel'
import type { AllAvatarColors, DefaultAvatarColors } from '@/constants/avatar'
import type ContactList from '@/data/models/domain/ContactList'
import type Contact from '@/data/models/domain/Contact'
import type { IconName } from '@/assets/icons/icons'
import type { TransportResponse } from '@/services/transport/types'
import type FilteredView from '@/data/models/domain/filteredViews/FilteredView'

export enum MessagesPriceCreationSource {
  CHAT = 'C',
}

export type MessagesPricePostBody = {
  contacts: string
  lists: string
  folders?: string
  filteredViews: string
  phones: string
  text: string
  from: string | null
  sendingDateTime?: string
  sendingTimezone?: string
  rrule?: string
  templateId?: number
  resources?: string
  destination?: 'tts' | 'mms'
  cutExtra?: number
  includeBlocked?: boolean
  fromType?: ServicePhoneType | null
  sourceType?: string
  creationSource?: MessagesPriceCreationSource
  labelId?: number
}

export type MessagesPostResponse = {
  id: number
  href: string
  type: string
  sessionId: number
  bulkId: number
  messageId: number
  scheduleId: number
  chatId: number
}

export type MessagesPriceCountry = {
  allowDedicated: boolean
  count: number
  landline: number
  max: number
  countryId: TmCountryCode
  countryName: string
  sum: string
}

export type MessagesPriceCountries = Nullable<Record<TmCountryCode, MessagesPriceCountry>>

export type MessagesPriceSendResponseData = {
  id: number
  messageId: number | null
  scheduleId: number | null
  bulkId: number | null
  sessionId: number | null
}
export type MessagesPriceSendResponseBody = TransportResponse<MessagesPriceSendResponseData>

export type MessagesPriceSpinners = 'recipients' | 'message'

export const symbolsLimitForCopying = 5000
export const bulkSessionNumber = 100
export const largeBulkSessionNumber = 1000
export const canadianRecipientsNumber = 500
export const contactListTempSize = 250
export const boundaryForContactListTemp = 100

type AvatarUrl = { url: string }
type AvatarIcon = {
  customIcon: IconName
  color: AllAvatarColors
  placeholderCustomSize: string
}

type AvatarName = {
  name: string | undefined
  color: AllAvatarColors
  placeholderCustomSize: string
}

export type RecipientAvatar = AvatarUrl | AvatarIcon | AvatarName

export type RecipientType =
  | 'contact'
  | 'list'
  | 'reply'
  | 'filtered_view'
  | 'contact_email'
  | 'email'
  | 'reply_email'
  | 'folder'

export type Recipient<T extends RecipientType = RecipientType> = {
  entityType: T
  entityId: number
  title: string
  value: string | null
  countryName: string
  avatarHref?: string | null
  sharedBy?: string
}

export type RecipientOptionBase<T> = {
  entityType: T
  label: string
  title?: string
  value: string | null
  entityId: number
  avatar?: RecipientAvatar
  hint?: string | boolean
  nameAsPhone?: boolean
  shortHint?: string
}

type RecipientOptionList = RecipientOptionBase<'list'> & {
  isTemporary: boolean
}
export const isRecipientOptionList = (recipientOption: RecipientOption): recipientOption is RecipientOptionList => {
  return recipientOption.entityType === 'list'
}

export type RecipientOptionContact = RecipientOptionBase<'contact'> & {
  sharedBy?: string
}
export const isRecipientOptionContact = (
  recipientOption: RecipientOption,
): recipientOption is RecipientOptionContact => {
  return recipientOption.entityType === 'contact'
}

export type RecipientOptionContactEmail = RecipientOptionBase<'contact_email'> & {
  sharedBy?: string
}
export const isRecipientOptionContactEmail = (
  recipientOption: RecipientOption,
): recipientOption is RecipientOptionContactEmail => {
  return recipientOption.entityType === 'contact_email'
}

type RecipientOptionFilteredView = RecipientOptionBase<'filtered_view'> & {
  sharedBy?: string
}
export const isRecipientOptionFilteredView = (
  recipientOption: RecipientOption,
): recipientOption is RecipientOptionFilteredView => {
  return recipientOption.entityType === 'filtered_view'
}

export type RecipientOptionFolder = RecipientOptionBase<'folder'>
export const isRecipientOptionFolder = (recipientOption: RecipientOption): recipientOption is RecipientOptionFolder => {
  return recipientOption.entityType === 'folder'
}

export type RecipientOption =
  | RecipientOptionBase<Exclude<RecipientType, 'list' | 'contact' | 'contact_email'> | 'raw'>
  | RecipientOptionContact
  | RecipientOptionContactEmail
  | RecipientOptionList
  | RecipientOptionFilteredView
  | RecipientOptionFolder

export type TInteractiveRecipientOption = InteractiveOption & RecipientOption

export interface IRecipientListProperties {
  id: string
  name: string
  membersCount: number
  isTemporary: boolean
}

export interface IRecipientContactProperties {
  id: string
  fullName: string
  phone: string | null
  blocked: boolean
}

export const recipientsFieldName = 'recipients'
export const senderFieldName = 'from'

export const messagesAttachmentFileModalId: TmWrappers = 'messagesAttachmentFileModal'
export const messagesAttachmentFilePreviewModalId: TmWrappers = 'messagesAttachmentFilePreviewModal'
export const messagesAttachmentSelectModalId: TmWrappers = 'messagesAttachmentSelectModal'

export type MessagesAttachmentFileResponseBody = {
  status: 201
  data: MessagesAttachmentFile
}

export const repeatTypeVariant = ['none', 'hourly', 'daily', 'weekly', 'monthly', 'yearly'] as const
export type RepeatType = (typeof repeatTypeVariant)[number]

export const endTypeVariant = ['never', 'after', 'date'] as const
export type EndType = (typeof endTypeVariant)[number]

export const ordinalDayVariant = ['First', 'Second', 'Third', 'Fourth', 'Last'] as const
export type OrdinalDay = (typeof ordinalDayVariant)[number]

export type MessageSendFormScheme = {
  recipients: RecipientOption[]
  text: JSONContent
  from: string
  schedule: MessageSendScheduleFormScheme
}

export interface MessageSendScheduleFormScheme {
  sendingDateTime: BaseScheduleFormSendDateTimeFormScheme
  sendingTimezone: string
  repeat: MessageSendRepeatFormScheme
}

export interface BaseScheduleFormSendDateTimeFormScheme {
  /**
   * ISO date format
   */
  date: string
  hours: number
  minutes: number
  /**
   * Only for 12-hour time format
   */
  timeIntervalType: HoursIntervalType
}

export interface MessageSendEndFormScheme {
  type: EndType
  smsSessions: number
  date: string
}

export interface MessageSendRepeatFormScheme {
  type: RepeatType
  end: MessageSendEndFormScheme
  hourly: MessageSendRepeatHourlyFormScheme
  daily: MessageSendRepeatDailyFormScheme
  weekly: MessageSendRepeatWeeklyFormScheme

  monthly: MessageSendRepeatMonthlyFormScheme

  yearly: MessageSendRepeatYearlyFormScheme
}

export interface MessageSendRepeatTypeFormScheme {
  count: number
}

export interface MessageSendRepeatHourlyFormScheme extends MessageSendRepeatTypeFormScheme {}

export interface MessageSendRepeatDailyFormScheme extends MessageSendRepeatTypeFormScheme {}

export interface MessageSendRepeatWeeklyFormScheme extends MessageSendRepeatTypeFormScheme {
  days: WeekDays[]
}

export interface MessageSendRepeatMonthlyFormScheme extends MessageSendRepeatTypeFormScheme {
  repeat: MonthlyRepeat
  monthDay: string
  firstLastDay: OrdinalDay
  weekDay: WeekDaysFull
}

export interface MessageSendRepeatYearlyFormScheme extends MessageSendRepeatTypeFormScheme {
  type: YearlyType
  month: Months
  monthDay: string
  firstLastDay: OrdinalDay
  weekDay: WeekDaysFull
  dayOfMonth: Months
}

export type MessagesInfo = {
  shortLink: Ref<string>
  isMaxLength: Ref<boolean>
  isNotEnoughBalance: Ref<boolean>
  isByocPlanLimitExceed: Ref<boolean>
  isBulkSession: Ref<boolean>
  isLargeBulkSession: Ref<boolean>
  are500canadianNumbersSelected: Ref<boolean>
  hasAmericanRecipients: Ref<boolean>
  deficitCreditAmount: Ref<number>
  smsInfo: Ref<SmsInfo>
}

export type MessagesInfoUpdates = {
  updateTotalCost: () => void
  updateRecipientsCount: () => void
  updateMessage: (message: string) => void
}

export type TUseMessagesInfo = MessagesInfo &
  MessagesInfoUpdates & {
    watchUpdateRecipientsCount: () => void
    handleSubmitError: (error: unknown) => void
  }

export type TScheduleData = {
  sendingDateTime: string
  sendingTimezone: string
  rrule: string | null
}
export type MessageContactRecipient = {
  id: number
  fullName: string
  phone: string
}
export type MessageListRecipient = {
  id: string
  name: string
  membersCount: number
}
export const messageFieldName = 'text'

export enum MessagesBulksProgressStatus {
  WAITING = 'w',
  COMPLETED = 'c',
  FAILED = 'f',
  NOT_ENOUGH_MONEY = 'l',
}

export enum MessagesBulksProgressLastErrorCode {
  SessionRejected = 'SessionRejected',
  LowBalance = 'LowBalance',
  NoRecipients = 'NoRecipients',
  InvalidRecipients = 'InvalidRecipients',
  EmptyText = 'EmptyText',
  UnexpectedError = 'UnexpectedError',
  ForbiddenCountry = 'ForbiddenCountry',
  Blocked = 'Blocked',
  Unsubscribed = 'Unsubscribed',
  InvalidNumber = 'InvalidNumber',
  InvalidFrom = 'InvalidFrom',
  NotUsFromNumber = 'NotUsFromNumber',
  BadFrFromNumber = 'BadFrFromNumber',
  BadAeFromNumber = 'BadAeFromNumber',
  RegulationTm = 'RegulationTm',
  RegulationAu = 'RegulationAu',
  RegulationGb = 'RegulationGb',
  ShortLinkInText = 'ShortLinkInText',
  SurveyNondedicated = 'SurveyNondedicated',
  TtsNotLvnFromNumber = 'TtsNotLvnFromNumber',
  RejectedMms = 'RejectedMms',
  UserVonageCarrierMmsFileExtensionRejected = 'UserVonageCarrierMmsFileExtensionRejected',
  UserCarrierMmsNotSupported = 'UserCarrierMmsNotSupported',
  TollFreeUnverified = 'TollFreeUnverified',
  TenDlcUnverified = 'TenDlcUnverified',
  TenDlcRejected = 'TenDlcRejected',
  SendingDisabledOnAccount = 'SendingDisabledOnAccount',
  PaidServicesDisabled = 'PaidServicesDisabled',
  ByocTwilio = 'ByocTwilio',
  ByocVonage = 'ByocVonage',
  ByocSinch = 'ByocSinch',
  NotActiveUserCarrierProvider = 'NotActiveUserCarrierProvider',
  NotActiveUserCarrierPhone = 'NotActiveUserCarrierPhone',
  NotActiveUserCarrierSenderId = 'NotActiveUserCarrierSenderId',
  UserHasNotByocAccess = 'UserHasNotByocAccess',
  UserCarrierPhoneHasNotCapability = 'UserCarrierPhoneHasNotCapability',
  NotExistUserCarrierSender = 'NotExistUserCarrierSender',
  Unsupported = 'Unsupported',
}

export type MessageTypeSerialized = Pick<MessagesPrice, 'total' | 'countries' | 'loading'>

export type MessageResolverParams = Pick<SingleModelResolverParams, 'id' | 'source'> & {
  direction?: ChatDirection
  modelType?: typeof BaseModel
  chatType?: ChatType
}

export type ParticipantContact = {
  type: 'contact' | 'contactEmail'
  contact: Pick<Contact, 'id' | 'phone' | 'email' | 'fullName' | 'unsubscribed'>
  avatar?: string
  avatarColor?: DefaultAvatarColors
}

export type ParticipantInfoContactCreatedCallback = () => void

type ParticipantDeletedContactWithPhone<T extends Nullable<ParticipantInfoContactCreatedCallback>> = {
  type: 'deletedContact'
  fullName: string
  phone: string
  avatarColor: DefaultAvatarColors
  contactCreatedCallback: T
}
type ParticipantDeletedContactFullName = {
  type: 'deletedContact'
  fullName: string
  phone?: undefined
}
type ParticipantDeletedContactEmpty = {
  type: 'deletedContact'
  fullName?: undefined
  phone?: undefined
}
export type ParticipantDeletedContact<T extends Nullable<ParticipantInfoContactCreatedCallback>> =
  | ParticipantDeletedContactWithPhone<T>
  | ParticipantDeletedContactFullName
  | ParticipantDeletedContactEmpty

export type ParticipantSenderId = {
  type: 'senderId'
  name: string
}
export type ParticipantFullName = {
  type: 'fullName'
  fullName: string
}
export type ParticipantContactList = {
  type: 'contactList'
  entity: Pick<ContactList, 'id' | 'name' | 'isTemporary'>
  avatarColor?: DefaultAvatarColors
}
export type ParticipantFilteredView = {
  type: 'filteredView'
  entity: FilteredView
  avatarColor?: DefaultAvatarColors
}

export type ParticipantPhone<T extends Nullable<ParticipantInfoContactCreatedCallback>> = {
  type: 'phone'
  phone: string
  avatarColor?: DefaultAvatarColors
  contactCreatedCallback: T
}
export type ParticipantRecipients = {
  type: 'recipients'
  name?: string
  count: number
}

export type ParticipantWhatsAppPhone<T extends Nullable<ParticipantInfoContactCreatedCallback>> = {
  type: 'whatsAppPhone'
  phone: string
  avatarColor?: DefaultAvatarColors
  contactCreatedCallback: T
}

export type ParticipantInfo<T extends Nullable<ParticipantInfoContactCreatedCallback> = null> =
  | ParticipantContact
  | ParticipantDeletedContact<T>
  | ParticipantContactList
  | ParticipantFilteredView
  | ParticipantPhone<T>
  | ParticipantRecipients
  | ParticipantSenderId
  | ParticipantFullName
  | ParticipantWhatsAppPhone<T>

export const messagesMaxQuantityRawRecipient = 250

/** A folder recipient as the api returns it: the name comes inline, so it needs no entity of its own */
export type FolderRecipient = {
  id: string
  name: string
  isDeleted: boolean
}

export interface MakeRecipientOptionsParams {
  removeBlockedContacts?: boolean
  /** Off by default: a form that cannot save folders back must not show them */
  withFolders?: boolean
}
